import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { usePurchaseAITokens, useAITokenPlans } from "../../../../../queries/billing";

const CURRENCY_SYMBOLS = { gbp: "£", usd: "$" };
const symbolFor = (code) => CURRENCY_SYMBOLS[(code || "gbp").toLowerCase()] || "";

const VIEW = { SELECT: "select", SUCCESS: "success", FAILED: "failed" };

const BuyTokensModal = ({ isOpen, onClose, userActiveLocation, onPurchased }) => {
  const { mutateAsync: purchaseAITokens } = usePurchaseAITokens(userActiveLocation?.id);
  const tokenPlansQuery = useAITokenPlans(userActiveLocation?.agency_id, isOpen);
  const [selected, setSelected] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [view, setView] = useState(VIEW.SELECT);
  const [errorMsg, setErrorMsg] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const loadingPacks = tokenPlansQuery.isFetching;
  const tokenPacks = (tokenPlansQuery.data || []).map((p) => ({
    tokens: Number(p.tokens || 0),
    price: Number(p.amount || 0),
    currency: (p.currency || "gbp").toLowerCase(),
  }));

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const reset = () => {
    setSelected(null);
    setView(VIEW.SELECT);
    setErrorMsg("");
    setProcessing(false);
    setDropdownOpen(false);
  };

  const handleClose = () => {
    if (processing) return;
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setProcessing(true);
    setErrorMsg("");
    const payload = {
      account_id: userActiveLocation?._id,
      amount: selected.price,
      tokens: selected.tokens,
      currency: selected.currency || "gbp",
    };
    try {
      const response = await purchaseAITokens(payload);
      if (response?.PaymentDone && response?.success) {
        setView(VIEW.SUCCESS);
        onPurchased && onPurchased(response?.data);
      } else {
        setErrorMsg(response?.message || "Your card could not be charged.");
        setView(VIEW.FAILED);
      }
    } catch (error) {
      setErrorMsg(error?.message || "Your card could not be charged.");
      setView(VIEW.FAILED);
    }
    setProcessing(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={handleClose}
    >
      <div
        className="bg-card w-[460px] max-w-[92vw] rounded-[10px] shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            {view === VIEW.SUCCESS && (
              <CheckCircleOutlineIcon style={{ fontSize: 24, color: "#16A34A" }} />
            )}
            {view === VIEW.FAILED && (
              <ErrorOutlineIcon style={{ fontSize: 24, color: "#DC2626" }} />
            )}
            <div>
              <b className="text-lg">
                {view === VIEW.SUCCESS && "You're all set"}
                {view === VIEW.FAILED && "We couldn't complete your token purchase"}
                {view === VIEW.SELECT && "Keep your Tasks running"}
              </b>
              {view === VIEW.SELECT && (
                <p className="text-sm text-muted-foreground mt-1">
                  Select your one-time AI Task  top-up.
                </p>
              )}
            </div>
          </div>
          <CloseIcon
            className="cursor-pointer text-muted-foreground flex-shrink-0"
            onClick={handleClose}
          />
        </div>

        {view === VIEW.SELECT && (
          <>
            <div className="mt-5 border !border-border rounded-[8px] p-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="relative inline-flex w-4 h-4 rounded-full border-2 border-primary items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-primary" />
                </span>
                <span className="text-sm font-semibold">Purchase AI Task </span>
              </label>

              <div ref={dropdownRef} className="relative mt-3">
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className={`w-full px-3 py-2 border !border-border rounded-[6px] text-sm bg-card flex justify-between items-center ${selected ? "text-foreground" : "text-muted-foreground"
                    }`}
                >
                  <span>
                    {selected
                      ? `${selected.tokens.toLocaleString()} AI Task· ${symbolFor(selected.currency)}${selected.price}`
                      : loadingPacks
                        ? "Loading packages..."
                        : tokenPacks.length === 0
                          ? "No packages available"
                          : "Select AI Task  package"}
                  </span>
                  <KeyboardArrowDownIcon
                    className={`text-muted-foreground transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {dropdownOpen && tokenPacks.length > 0 && (
                  <ul className="absolute left-0 right-0 top-[calc(100%+6px)] bg-card border !border-border rounded-[6px] shadow-lg z-10 overflow-hidden">
                    {tokenPacks.map((pack) => {
                      const isSelected = selected?.tokens === pack.tokens && selected?.currency === pack.currency;
                      return (
                        <li key={`${pack.tokens}-${pack.currency}`}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelected(pack);
                              setDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2.5 text-sm transition-colors hover:bg-muted ${isSelected ? "bg-muted font-semibold" : "text-foreground"
                              }`}
                          >
                            {pack.tokens.toLocaleString()} AI Task · {symbolFor(pack.currency)}{pack.price}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={handleClose}
                disabled={processing}
                className="px-5 py-2 text-sm border !border-border rounded-[6px] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={processing || !selected}
                className="px-5 py-2 text-sm bg-primary rounded-[6px] text-primary-foreground disabled:opacity-60"
              >
                {processing ? "Processing..." : "Confirm"}
              </button>
            </div>
          </>
        )}

        {view === VIEW.SUCCESS && (
          <>
            <p className="text-sm text-muted-foreground mt-2 ml-8">
              Your AI Task are ready to use.
            </p>
            <div className="flex justify-end mt-6">
              <button
                onClick={handleClose}
                className="px-5 py-2 text-sm bg-primary rounded-[6px] text-primary-foreground"
              >
                Done
              </button>
            </div>
          </>
        )}

        {view === VIEW.FAILED && (
          <>
            <p className="text-sm text-muted-foreground mt-2 ml-8">
              {errorMsg ||
                "Please update your payment details on the Billing page and try again."}
            </p>
            <div className="flex justify-end mt-6">
              <button
                onClick={handleClose}
                className="px-5 py-2 text-sm bg-primary rounded-[6px] text-primary-foreground"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuyTokensModal;
